import { format } from "date-fns";
import { utc } from "@date-fns/utc";
import { joinWithConjunction } from "../../utilities/lists";
import {
  AccountStatus,
  StatusType,
  type AppAccount,
} from "../../state/api.types";

/**
 * Ordered list of status types for consistent display across the app.
 * This determines the order in filter dropdowns and other UI elements.
 */
export const ORDERED_STATUS_TYPES: StatusType[] = [
  StatusType.Created,
  StatusType.Applying,
  StatusType.Reviewing,
  StatusType.NeedsAttention,
  StatusType.Scheduled,
  StatusType.Activating,
  StatusType.Active,
  StatusType.Cancelled,
];

type StatusDisplay = {
  label: string;
  badgeColor: "success" | "error" | "warning" | "cyan" | "gray";
  dotColor: string;
};

const UNKNOWN_STATUS_DISPLAY: StatusDisplay = {
  label: "Unknown",
  badgeColor: "gray",
  dotColor: "text-fg-quaternary",
};

/**
 * Map of status types to their display properties.
 * This is the single source of truth for status display across the app.
 */
const STATUS_DISPLAY_MAP: Record<StatusType, StatusDisplay> = {
  [StatusType.Active]: {
    label: "Active",
    badgeColor: "success",
    dotColor: "text-fg-success-secondary",
  },
  [StatusType.Activating]: {
    label: "Activating",
    badgeColor: "success",
    dotColor: "text-fg-success-secondary",
  },
  [StatusType.Scheduled]: {
    label: "Scheduled",
    badgeColor: "success",
    dotColor: "text-fg-success-secondary",
  },
  [StatusType.Reviewing]: {
    label: "Reviewing",
    badgeColor: "cyan",
    dotColor: "text-fg-brand-accent-cyan",
  },
  [StatusType.NeedsAttention]: {
    label: "Needs attention",
    badgeColor: "warning",
    dotColor: "text-fg-warning-secondary",
  },
  [StatusType.Applying]: {
    label: "Applying",
    badgeColor: "cyan",
    dotColor: "text-fg-brand-accent-cyan",
  },
  [StatusType.Created]: {
    label: "Created",
    badgeColor: "cyan",
    dotColor: "text-fg-brand-accent-cyan",
  },
  [StatusType.Cancelled]: {
    label: "Cancelled",
    badgeColor: "gray",
    dotColor: "text-fg-quaternary",
  },
};

export function getStatusDisplay(status: string): StatusDisplay {
  return Object.hasOwn(STATUS_DISPLAY_MAP, status)
    ? STATUS_DISPLAY_MAP[status as StatusType]
    : UNKNOWN_STATUS_DISPLAY;
}

/**
 * Get the tooltip text for an account status based on the account's status_reason.
 * Returns null if no tooltip should be shown for the status.
 */
export function getAccountStatusTooltip(account: AppAccount): string | null {
  const statusReason = account.status_reason;

  // Get service start date if available
  const serviceStartDate = account.locations[0]?.service_start_date
    ? format(account.locations[0].service_start_date, "PPP", { in: utc })
    : null;

  // Get service end date if available
  const serviceEndDate = account.locations[0]?.final_service_date
    ? format(account.locations[0].final_service_date, "PPP", { in: utc })
    : null;

  // Map status reasons to tooltip messages
  switch (statusReason) {
    // Missing steps - combine into one message
    case AccountStatus.MissingAddress:
    case AccountStatus.MissingIdentity:
    case AccountStatus.MissingPaymentMethod:
    case AccountStatus.PlanNotAccepted: {
      const missingItems: string[] = [];
      if (statusReason === AccountStatus.MissingAddress) {
        missingItems.push("provided an address");
      }
      if (
        statusReason === AccountStatus.PlanNotAccepted ||
        !account.enrollment.has_accepted_plan
      ) {
        missingItems.push("accepted the plan");
      }
      if (
        statusReason === AccountStatus.MissingIdentity ||
        !account.enrollment.is_identity_verified
      ) {
        missingItems.push("verified their identity");
      }
      if (
        statusReason === AccountStatus.MissingPaymentMethod ||
        !account.enrollment.has_payment_method
      ) {
        missingItems.push("provided a payment method");
      }

      if (missingItems.length === 0) {
        return "Customer has provided a payment method and verified their identity, but not completed enrollment.";
      }

      return `Customer has not ${joinWithConjunction(missingItems, "and")}.`;
    }

    case AccountStatus.EnrollmentNotFinalized:
      return "Customer has completed all enrollment steps but enrollment has not been finalized yet.";

    // Reviewing statuses
    case AccountStatus.VerificationPending:
    case AccountStatus.CreditCheckPending:
      return "Customer's enrollment is being verified by us.";

    // Credit-related statuses
    case AccountStatus.CreditCheckFrozen:
      return "Customer has been emailed to let us know when they unfreeze their credit so we can complete a credit check.";

    case AccountStatus.CreditNotFound:
      return "Customer has been emailed to provide additional credit proof points because their credit was not found.";

    case AccountStatus.CreditDeclined:
      return "Customer has been emailed to provide additional credit proof points because their credit score was low.";

    case AccountStatus.CreditCheckError:
      return "Credit provider was unable to check the customer's credit, but we'll continue trying to check the credit.";

    // Verification hold
    case AccountStatus.EnrollmentBlockedVerificationHold:
      return "Customer has been emailed to provide additional identity verification.";

    // Switch hold
    case AccountStatus.EnrollmentBlockedSwitchHold:
      return "Customer has a switch hold from another retail energy provider. We've emailed them to let them know they'll need to settle their balance with the other provider first.";

    // Deactivated location
    case AccountStatus.EnrollmentBlockedDeactivated:
      return "This account's location has been deactivated and enrollment is blocked.";

    // Utility error
    case AccountStatus.UtilityBlocked:
      return "We're working on an issue that occurred when trying to activate the customer's meter.";

    // Scheduled - enrollment intent recorded, waiting for next step
    case AccountStatus.EnrollmentPendingClientAgent:
      return "Customer has provided all needed information and internal checks have passed. Plan details will been sent to customer for approval once you accept a plan on their behalf.";

    case AccountStatus.EnrollmentPendingDeferred:
      return "Customer has provided all needed information and internal checks have passed. Plan details will been sent to customer for approval once you request a plan.";

    // Scheduled - all checks cleared, waiting to send switch to utility
    case AccountStatus.ServicePending:
      if (serviceStartDate) {
        return `Customer's enrollment has been approved and their service is scheduled to start on ${serviceStartDate}.`;
      }
      return "Customer's enrollment has been approved and their service is scheduled to start.";

    // Activating - switch sent to utility, waiting for service to start
    case AccountStatus.UtilitySubmitted:
      return "Customer's enrollment has been submitted to their local utility for processing.";

    // Service active
    case AccountStatus.Active:
      if (serviceStartDate) {
        return `Service started on ${serviceStartDate}.`;
      }
      return "Service is active.";

    // Cancelled statuses
    case AccountStatus.CancelledPreStart:
      return "We received a request to cancel enrollment before service started.";

    case AccountStatus.CancelledPostStart:
      if (serviceEndDate) {
        return `We received a request to cancel service on ${serviceEndDate}.`;
      }
      return "We received a request to cancel service.";

    // Expired
    case AccountStatus.ExpirePreActive:
      return "Customer did not take additional steps needed for the credit check.";

    // Unresolved or unknown status
    case AccountStatus.Unresolved:
    default:
      return null;
  }
}
